import type { SlugLanguageConfig } from '../../../shared/utils/slugify-segment';
import { slugifySegment } from '../../../shared/utils/slugify-segment';
import { CategoryNodeId } from './category-node-id';

// Swedish ä/å/ö fold to a/a/o via NFD normalization (no digraph overrides needed).
// Stop words verified against live portal category names: "Barn och föräldrar" -> barn-foraldrar.
const SWEDISH_SLUG_CONFIG: SlugLanguageConfig = {
  digraphs: {},
  stopWords: new Set([
    'och',
    'eller',
    'för',
    'av',
    'med',
    'i',
    'på',
    'en',
    'ett',
    'den',
    'det',
    'de',
    'som',
    'vid',
    'om',
    'från',
    'till',
  ]),
};

export interface CategoryNodeBuildError {
  readonly kind: 'CategoryNodeBuildError';
  readonly portalSlug: string;
  readonly reason: string;
}

export interface CategoryNodeMap {
  readonly buildErrors: readonly CategoryNodeBuildError[];

  portalSlugToId(portalSlug: string): CategoryNodeId | undefined;
  accessKeyToId(accessKey: string): CategoryNodeId | undefined;
  medpdb2PathToId(path: string): CategoryNodeId | undefined;

  // Reverse lookups are map-based and only valid for rows loaded at build time.
  idToPortalSlug(id: CategoryNodeId): string | undefined;
  idToAccessKey(id: CategoryNodeId): string | undefined;
}

function slugifyPortalPath(portalSlug: string): CategoryNodeId | null {
  const segments = portalSlug.split('|');
  const slugifiedSegments = segments.map((seg) => slugifySegment(seg, SWEDISH_SLUG_CONFIG));
  if (slugifiedSegments.some((s) => s === '')) {
    return null;
  }
  return slugifiedSegments.join('/') as CategoryNodeId;
}

export function buildCategoryNodeMap(rows: readonly { readonly slug: string }[]): CategoryNodeMap {
  const reverseMap = new Map<string, string>();
  const buildErrors: CategoryNodeBuildError[] = [];

  for (const row of rows) {
    const canonicalId = slugifyPortalPath(row.slug);
    if (canonicalId === null) {
      buildErrors.push({
        kind: 'CategoryNodeBuildError',
        portalSlug: row.slug,
        reason: `One or more segments in "${row.slug}" produce an empty slug after slugification`,
      });
      continue;
    }
    if (!reverseMap.has(canonicalId)) {
      reverseMap.set(canonicalId, row.slug);
    }
  }

  return {
    buildErrors,

    portalSlugToId(portalSlug: string): CategoryNodeId | undefined {
      return slugifyPortalPath(portalSlug) ?? undefined;
    },

    accessKeyToId(accessKey: string): CategoryNodeId | undefined {
      const portalSlug = accessKey.replace(/_/g, '|');
      return slugifyPortalPath(portalSlug) ?? undefined;
    },

    medpdb2PathToId(path: string): CategoryNodeId | undefined {
      const assembled = path.replace(/_/g, '/').toLowerCase().trim();
      if (!assembled) {
        return undefined;
      }
      return CategoryNodeId(assembled);
    },

    idToPortalSlug(id: CategoryNodeId): string | undefined {
      return reverseMap.get(id);
    },

    idToAccessKey(id: CategoryNodeId): string | undefined {
      const portalSlug = reverseMap.get(id);
      return portalSlug !== undefined ? portalSlug.replace(/\|/g, '_') : undefined;
    },
  };
}
